import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CircularProgress } from "@mui/joy";
import { useGetCurrentDealQuery, useGetEmpresaByIdQuery } from "../../empresaApi";
import { ChangeDealStage } from "../../../../components/EmpresaComponents/ChangeDealStage";
import { StageProgressDealBar } from "./StageProgressDealBar";
import { useLocation } from "react-router";
import { MainContact } from "./MainContact";
import { VisitasGeneralInfo } from "./VisitasGeneralInfo";
import { EmpresaName } from "./EmpresaName";
import { PageNav } from "./PageNav";
import { setGeneralInfo } from "../../../../store/empresaSelectedSlice";
import { NoActiveDealMenu } from "../../../../components/EmpresaComponents/NoActiveDealMenu";
import { EmpresaOptionMenu } from "./EmpresaOptionMenu";
import { selectCurrentUserRole } from "../../../../api/auth/authSlice";
import { ROLES } from "../../../../utils/enums";
import { isFetchBaseQueryError } from "src/utils/errorHelpers";
import { useErrorHandling } from "src/features/empresas/EmpresaDetails/context/ErrorHandlingContext";

interface Props {
    empresaId: number;
}

export function EmpresaGeneralInfo({ empresaId }: Props) {
    const { reportError } = useErrorHandling();
    const dispatch = useDispatch();
    const userRole = useSelector(selectCurrentUserRole);

	const {
        data: empresa,
        isLoading,
        isSuccess: empresaSuccess,
        error,
    } = useGetEmpresaByIdQuery(empresaId, { refetchOnMountOrArgChange: true });

    const {
        data: currentDeal,
        isSuccess
    } = useGetCurrentDealQuery(empresaId, { refetchOnMountOrArgChange: true });

    const location = useLocation();
    const state = location.state;

    let fromName = "empresas";
    let fromPath = "/empresa"
    if(state != null && state.fromName && state.fromPath) {
        fromName = state.fromName;
        fromPath = state.fromPath;
    }

    if (isFetchBaseQueryError(error)) {
        if(typeof error.status == "number")
            reportError(error.status);
    }

    useEffect(() => {
        dispatch(setGeneralInfo(empresa));
    }, [empresa])

    return (
        <div className="border-b-2 border-b-gray-300">

            {isLoading || !empresaSuccess
            ? <div className="flex justify-center items-center h-[170px]"><CircularProgress/></div>
            : <>
            <div className="flex col-span-3 row-span-1 min-[830px]:h-[100px] justify-evenly max-[830px]:flex-col">

                <EmpresaName
                    empresa={empresa}/>

                <div className="border border-gray-400 my-4 max-[1100px]:hidden"></div>

                <MainContact 
                    empresa={empresa}/>

                <div className="border border-gray-400 my-4"></div>

                <div className="sm:max-w-[900px] sm:ws-3/6 sm:w-full sm:min-w-[550px]">

                    <div className="flex h-1/2 gap-2 justify-between items-center px-3">
                        <div className="flex gap-2">
                            <div className="rounded-full bg-gray-400 h-6 w-6"></div>
                            <p className="font-semibold">{empresa?.nombreProspectador ?? "Sin Prospectador Asignado"}</p>
                        </div>
                        <div className="flex gap-x-2">
                            <div className="flex border-2 border-gray-300 rounded">
                                <div className="flex items-center w-[285px]">
                                    {isSuccess && currentDeal?.activeDeal
                                    ? <ChangeDealStage 
                                        nombreProspectador={empresa?.nombreProspectador ?? "Sin Prospectador"}
                                        currentFechaVisita={empresa?.proximaVisita ?? ""}
                                        empresaId={empresaId}
                                        pipelineId={currentDeal.pipelineId}
                                        dealId={currentDeal.dealId}
                                        currentStageId={currentDeal.currentStageId}
                                        estadoEmpresa={empresa.estado}/>
                                    :
                                    <NoActiveDealMenu 
                                        empresaId={empresaId}/>
                                    }
                                </div> 

                                <div className="h-[32px] border-x border-y-0 border-gray-300 rounded"></div>

                                <div className="flex items-center px-[10px] py-1">
                                    <p className="font-semibold text-sm">{empresa?.estado}</p>
                                </div>
                            </div>

                            {userRole == ROLES.ADMIN && (
                                <EmpresaOptionMenu />
                            )}
                        </div>
                    </div>

                    <VisitasGeneralInfo 
                        empresa={empresa}/>

                </div>
            </div>

            <div className="hidden sm:block">
                {isSuccess && (
                    <StageProgressDealBar 
                        currentDeal={currentDeal}/>
                )}
            </div>

            <div className="flex flex-col mx-4 my-2">
                <div className="flex justify-between">
                    <PageNav
                        fromName={fromName}
                        fromPath={fromPath} />
                </div>
            </div>
            </>}

        </div>
    );
}